import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowButton } from "@/components/ArrowButton";
import { LineReveal, FadeUp } from "@/components/Reveal";
import RootLine from "@/components/RootLine";
import { waLink, PROFESSIONAL } from "@/data/site";

const Hero = () => {
  const photoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: photoRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-30, 50]);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden pt-28 lg:pt-36">
      <div
        aria-hidden="true"
        className="absolute -right-40 top-0 h-[560px] w-[560px] rounded-full bg-sand/70 lg:right-[-120px] lg:top-[-80px]"
      />
      <div className="container-x relative grid items-center gap-14 pb-16 lg:grid-cols-12 lg:gap-8 lg:pb-24">
        <div className="lg:col-span-7">
          <FadeUp y={16}>
            <div className="flex items-center gap-3">
              <img src="/assets/tree-mark.png" alt="" className="h-7 w-auto" width="28" height="28" />
              <p className="eyebrow" data-testid="hero-eyebrow">Raízes</p>
            </div>
          </FadeUp>

          <h1 className="mt-8 font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <LineReveal delay={0.15}>Compreender também</LineReveal>
            <LineReveal delay={0.28}>é uma forma</LineReveal>
            <LineReveal delay={0.41}>
              de <em className="font-normal italic text-raiz-700">transformar.</em>
            </LineReveal>
          </h1>

          <FadeUp delay={0.55}>
            <p className="lead mt-7 max-w-xl" data-testid="hero-subheadline">
              Psicologia Clínica e Neuropsicologia para crianças, adolescentes e adultos, com atendimento presencial e online.
            </p>
          </FadeUp>

          <FadeUp delay={0.65}>
            <div className="mt-7 border-l-2 border-gold-500/70 pl-5" data-testid="hero-professional-id">
              <p className="font-serif text-lg text-raiz-900">{PROFESSIONAL.name}</p>
              <p className="text-sm text-ink/60">{PROFESSIONAL.titles} — {PROFESSIONAL.crp}</p>
            </div>
          </FadeUp>

          <FadeUp delay={0.75} className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
            <ArrowButton href={waLink()} target="_blank" rel="noopener noreferrer" testId="hero-agendar-button">
              Agendar uma conversa
            </ArrowButton>
            <ArrowButton to="/psicoterapia" variant="outline" testId="hero-atendimentos-button">
              Conhecer os atendimentos
            </ArrowButton>
          </FadeUp>
        </div>

        <div className="relative lg:col-span-5" ref={photoRef}>
          <div aria-hidden="true" className="absolute -left-6 top-8 h-full w-full rounded-t-[999px] bg-beige/70" />
          <FadeUp delay={0.35} y={0}>
            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.3, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-t-[999px]"
            >
              <motion.img
                src="/assets/sabrina-hero.jpg"
                alt="Sabrina Mota, psicóloga e neuropsicóloga, sentada com roupa branca e blazer verde"
                data-testid="hero-photo"
                style={{ y, objectPosition: "center 22%" }}
                className="aspect-[4/5] w-full scale-110 object-cover"
                width="1200"
                height="1500"
              />
            </motion.div>
          </FadeUp>
          <FadeUp delay={0.9} className="absolute -left-3 bottom-10 sm:-left-8">
            <div
              data-testid="hero-badge"
              className="rounded-2xl border border-gold-300/60 bg-cream/80 px-5 py-4 shadow-lg shadow-raiz-900/10 backdrop-blur-md"
            >
              <p className="font-serif text-base text-raiz-900">{PROFESSIONAL.name}</p>
              <p className="mt-0.5 text-xs text-ink/60">{PROFESSIONAL.titles} — {PROFESSIONAL.crp}</p>
            </div>
          </FadeUp>
          <FadeUp delay={1}>
            <p className="mt-4 text-right text-xs tracking-wide text-ink/45">Presencial &amp; Online</p>
          </FadeUp>
        </div>
      </div>
      <RootLine className="relative -mb-2 h-20 lg:h-28" />
    </section>
  );
};

export default Hero;
